import { uiText } from '../../utils/i18n';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useStore } from '../../store';
import QRCode from 'qrcode';
import {
  ToolConnectionModal,
  type ToolConnectionSummary,
  type ToolConnectionType,
} from './ToolConnectionModal';
import styles from './ConnectionsPanel.module.css';

interface ConnectionsPanelProps {
  botName: string;
}

/* ────── Brand SVG logos ────── */

function FeishuLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <path d="M7.876 7.886c2.78-4.392 9.72-5.196 14.832-1.776l.42.288c-4.14 1.68-7.98 4.404-11.16 7.956l-.312.36-3.78-6.828z" fill="#00D6B9"/>
      <path d="M23.128 6.398c5.064 3.468 8.844 9.696 9.372 16.392l.024.408-17.7 12.516c-.648.456-1.536.072-1.632-.696l-.006-.12V18.534c0-1.044.324-2.064.924-2.916l.168-.228 8.85-8.992z" fill="#3370FF"/>
      <path d="M32.524 23.198c.528 6.696-1.812 13.272-6.396 18.06l-.36.372-12.06-5.904 1.116-.828 17.7-11.7z" fill="#00D6B9"/>
    </svg>
  );
}

function WeChatLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <path d="M18.6 6C10.98 6 4.8 11.22 4.8 17.7c0 3.66 1.92 6.96 4.92 9.18l-1.26 3.72c-.12.36.3.66.6.48l4.56-2.52c1.44.42 2.94.6 4.5.6.54 0 1.08-.024 1.62-.072a10.8 10.8 0 01-.36-2.7c0-6.48 6.12-11.7 13.68-11.7.54 0 1.08.024 1.62.084C32.28 9.36 25.98 6 18.6 6z" fill="#2DC100"/>
      <circle cx="14.4" cy="15.6" r="1.8" fill="#fff"/>
      <circle cx="22.8" cy="15.6" r="1.8" fill="#fff"/>
      <path d="M44.4 26.4c0-5.46-5.16-9.9-11.52-9.9s-11.52 4.44-11.52 9.9 5.16 9.9 11.52 9.9c1.32 0 2.58-.18 3.78-.48l3.6 1.98c.24.12.54-.06.48-.36l-.96-2.94c2.64-1.92 4.62-4.74 4.62-8.1z" fill="#2DC100"/>
      <circle cx="28.8" cy="25.8" r="1.44" fill="#fff"/>
      <circle cx="37.2" cy="25.8" r="1.44" fill="#fff"/>
    </svg>
  );
}

function DingTalkLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <path d="M24 4C12.954 4 4 12.954 4 24s8.954 20 20 20 20-8.954 20-20S35.046 4 24 4z" fill="#3296FA"/>
      <path d="M34.08 22.56c-.24.06-2.1.66-2.1.66s1.14 1.08 1.68 1.86c.06.06.12.18.12.3.06.36-.18.6-.42.72l-2.22.84.06.06c.12.06.24.18.24.36 0 .24-.18.48-.6.54l-4.08.78-1.02 3.54-.06.12c-.12.24-.42.48-.84.3 0 0-2.22-1.08-2.76-1.38l-1.38 1.62c-.24.3-.54.42-.78.42-.12 0-.24-.06-.3-.06-.36-.12-.48-.48-.42-.84l.48-3.12-6.66-2.94c-.54-.24-.6-.72-.12-.96l17.1-8.58c.36-.18.84-.06.96.36.06.18 0 .42-.12.6l-3.06 4.02 5.76-1.44c.72-.18 1.08.54.48.96z" fill="#fff"/>
    </svg>
  );
}

function WeComLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <path d="M19.2 7.2c-7.2 0-13.2 5.28-13.2 12 0 3.72 1.8 7.08 4.68 9.36l-1.08 4.08c-.06.3.24.54.48.36l4.44-2.76c1.56.48 3.24.72 4.92.72.36 0 .72-.012 1.08-.036a11.76 11.76 0 01-.48-3.36c0-6.36 5.64-11.52 12.6-11.52.48 0 .96.024 1.44.072C33 10.68 26.64 7.2 19.2 7.2z" fill="#2BAD13"/>
      <circle cx="14.4" cy="17.4" r="1.68" fill="#fff"/>
      <circle cx="22.2" cy="17.4" r="1.68" fill="#fff"/>
      <path d="M32.64 18.24c-5.88 0-10.68 4.2-10.68 9.36s4.8 9.36 10.68 9.36c1.44 0 2.82-.24 4.08-.72l3.48 2.16c.18.12.42-.06.36-.3l-.84-3.18c2.4-1.92 3.96-4.68 3.96-7.68 0-4.8-4.92-9-11.04-9z" fill="#3DA8F5"/>
      <circle cx="28.8" cy="27" r="1.44" fill="#fff"/>
      <circle cx="36.6" cy="27" r="1.44" fill="#fff"/>
    </svg>
  );
}

function TelegramLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <path d="M24 4C12.954 4 4 12.954 4 24s8.954 20 20 20 20-8.954 20-20S35.046 4 24 4z" fill="#29B6F6"/>
      <path d="M33.6 15.48l-3.18 15c-.24 1.02-.84 1.26-1.68.78l-4.68-3.42-2.28 2.16c-.24.24-.48.48-.96.48l.36-4.86 8.7-7.86c.36-.36-.06-.54-.6-.18L18.3 24.9l-4.56-1.44c-1.02-.3-1.02-.96.24-1.44l17.76-6.84c.84-.3 1.56.18 1.26 1.44l.6-1.14z" fill="#fff"/>
    </svg>
  );
}

function SlackLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <path d="M13.2 28.8a3.6 3.6 0 01-3.6 3.6 3.6 3.6 0 01-3.6-3.6 3.6 3.6 0 013.6-3.6h3.6v3.6zm1.8 0a3.6 3.6 0 013.6-3.6 3.6 3.6 0 013.6 3.6v9a3.6 3.6 0 01-3.6 3.6 3.6 3.6 0 01-3.6-3.6v-9z" fill="#E01E5A"/>
      <path d="M18.6 13.2a3.6 3.6 0 01-3.6-3.6A3.6 3.6 0 0118.6 6a3.6 3.6 0 013.6 3.6v3.6h-3.6zm0 1.8a3.6 3.6 0 013.6 3.6 3.6 3.6 0 01-3.6 3.6H9.6A3.6 3.6 0 016 18.6a3.6 3.6 0 013.6-3.6h9z" fill="#36C5F0"/>
      <path d="M34.8 18.6a3.6 3.6 0 013.6-3.6 3.6 3.6 0 013.6 3.6 3.6 3.6 0 01-3.6 3.6h-3.6v-3.6zm-1.8 0a3.6 3.6 0 01-3.6 3.6 3.6 3.6 0 01-3.6-3.6V9.6A3.6 3.6 0 0129.4 6a3.6 3.6 0 013.6 3.6v9z" fill="#2EB67D"/>
      <path d="M29.4 34.8a3.6 3.6 0 013.6 3.6 3.6 3.6 0 01-3.6 3.6 3.6 3.6 0 01-3.6-3.6v-3.6h3.6zm0-1.8a3.6 3.6 0 01-3.6-3.6 3.6 3.6 0 013.6-3.6h9a3.6 3.6 0 013.6 3.6 3.6 3.6 0 01-3.6 3.6h-9z" fill="#ECB22E"/>
    </svg>
  );
}

function DiscordLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <path d="M38.16 11.16A35.1 35.1 0 0029.46 8.4a.132.132 0 00-.14.066c-.378.672-.798 1.548-1.092 2.238a32.4 32.4 0 00-9.462 0 24.12 24.12 0 00-1.11-2.238.138.138 0 00-.14-.066 35.04 35.04 0 00-8.7 2.76.126.126 0 00-.058.048C4.182 18.6 2.73 25.8 3.444 32.91a.144.144 0 00.054.1 35.28 35.28 0 0010.632 5.4.138.138 0 00.15-.048 25.56 25.56 0 002.196-3.576.138.138 0 00-.072-.192 23.28 23.28 0 01-3.33-1.59.138.138 0 01-.012-.228c.222-.168.444-.342.66-.516a.132.132 0 01.138-.018c6.984 3.192 14.544 3.192 21.456 0a.132.132 0 01.138.018c.216.174.438.348.66.516a.138.138 0 01-.012.228 21.84 21.84 0 01-3.33 1.59.138.138 0 00-.072.192c.648 1.254 1.386 2.448 2.196 3.576a.132.132 0 00.15.048 35.16 35.16 0 0010.644-5.4.138.138 0 00.054-.096c.852-8.826-1.428-16.488-6.048-23.286a.108.108 0 00-.054-.048zM17.1 29.16c-2.01 0-3.666-1.848-3.666-4.116s1.62-4.116 3.666-4.116c2.064 0 3.702 1.866 3.666 4.116 0 2.268-1.62 4.116-3.666 4.116zm13.548 0c-2.01 0-3.666-1.848-3.666-4.116s1.62-4.116 3.666-4.116c2.064 0 3.702 1.866 3.666 4.116 0 2.268-1.602 4.116-3.666 4.116z" fill="#5865F2"/>
    </svg>
  );
}

function WhatsAppLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <path d="M4.8 43.2l2.76-10.08A18.72 18.72 0 015.4 24C5.4 13.74 13.74 5.4 24 5.4S42.6 13.74 42.6 24 34.26 42.6 24 42.6a18.6 18.6 0 01-8.94-2.28L4.8 43.2z" fill="#25D366"/>
      <path d="M4.8 43.2l2.76-10.08A18.72 18.72 0 015.4 24C5.4 13.74 13.74 5.4 24 5.4S42.6 13.74 42.6 24 34.26 42.6 24 42.6a18.6 18.6 0 01-8.94-2.28L4.8 43.2z" fill="#25D366"/>
      <path d="M24 8.4c-8.58 0-15.6 6.96-15.6 15.6 0 2.94.84 5.76 2.4 8.22l.36.6-1.56 5.7 5.88-1.56.6.36A15.48 15.48 0 0024 39.6c8.58 0 15.6-6.96 15.6-15.6S32.58 8.4 24 8.4z" fill="#fff"/>
      <path d="M18.36 15.6c-.36-.84-.78-.84-1.14-.84h-.96c-.36 0-.9.12-1.38.6-.48.48-1.8 1.74-1.8 4.26s1.86 4.92 2.1 5.28c.24.36 3.66 5.58 8.82 7.62 4.32 1.68 5.16 1.38 6.12 1.26.96-.12 3.06-1.26 3.48-2.46.42-1.2.42-2.28.3-2.46-.12-.18-.48-.3-.96-.54-.48-.24-2.88-1.44-3.36-1.56-.48-.18-.78-.24-1.14.24-.36.48-1.32 1.68-1.62 1.98-.3.36-.6.36-1.08.12-.48-.24-2.04-.78-3.84-2.4-1.44-1.26-2.34-2.82-2.64-3.3-.3-.48 0-.78.24-.96.18-.18.48-.48.66-.72.18-.24.3-.42.42-.72.18-.3.06-.6-.06-.84-.12-.24-1.08-2.64-1.5-3.6z" fill="#25D366"/>
    </svg>
  );
}

function TeamsLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <path d="M31.2 16.8h10.2a1.8 1.8 0 011.8 1.8v9.6a5.4 5.4 0 01-5.4 5.4h-.6a5.4 5.4 0 01-5.4-5.4V17.4a.6.6 0 01.6-.6h-.6.6z" fill="#5059C9"/>
      <circle cx="37.8" cy="12" r="3.6" fill="#5059C9"/>
      <path d="M27.6 16.8H14.4a1.8 1.8 0 00-1.8 1.8v11.4a8.4 8.4 0 008.4 8.4h0a8.4 8.4 0 008.4-8.4V18.6a1.8 1.8 0 00-1.8-1.8z" fill="#7B83EB"/>
      <circle cx="21" cy="10.8" r="4.8" fill="#7B83EB"/>
    </svg>
  );
}

function GitLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <path d="M24 4.8 43.2 24 24 43.2 4.8 24 24 4.8Z" fill="#F05032" />
      <path d="m17.2 17.2 13.6 13.6M20.4 20.4v9.2" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
      <circle cx="17.2" cy="17.2" r="3.2" fill="#fff" />
      <circle cx="30.8" cy="30.8" r="3.2" fill="#fff" />
      <circle cx="20.4" cy="29.6" r="3.2" fill="#fff" />
    </svg>
  );
}

function DatabaseLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <ellipse cx="24" cy="10" rx="15" ry="6" fill="#5A8AB8" />
      <path d="M9 10v10c0 3.3 6.7 6 15 6s15-2.7 15-6V10" fill="#A8C2DB" stroke="#5A8AB8" strokeWidth="2" />
      <path d="M9 20v10c0 3.3 6.7 6 15 6s15-2.7 15-6V20" fill="#A8C2DB" stroke="#5A8AB8" strokeWidth="2" />
      <path d="M9 30v8c0 3.3 6.7 6 15 6s15-2.7 15-6v-8" fill="#A8C2DB" stroke="#5A8AB8" strokeWidth="2" />
      <path d="M9 20c0 3.3 6.7 6 15 6s15-2.7 15-6M9 30c0 3.3 6.7 6 15 6s15-2.7 15-6" stroke="#fff" strokeWidth="1.6" opacity=".8" />
    </svg>
  );
}

function GuanDataLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <rect x="5" y="5" width="38" height="38" rx="10" fill="#6B5DD3" />
      <path d="M15 31V20M24 31V13M33 31v-7" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
      <path d="M13 35h22" stroke="#BEB8F4" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function FigmaLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <path d="M16 5h8v14h-8a7 7 0 0 1 0-14Z" fill="#F24E1E" />
      <path d="M24 5h8a7 7 0 1 1 0 14h-8V5Z" fill="#FF7262" />
      <path d="M16 19h8v14h-8a7 7 0 0 1 0-14Z" fill="#A259FF" />
      <circle cx="31" cy="26" r="7" fill="#1ABCFE" />
      <path d="M16 33h8v7a7 7 0 1 1-8-7Z" fill="#0ACF83" />
    </svg>
  );
}

function PencilLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <rect x="5" y="5" width="38" height="38" rx="11" fill="#171717" />
      <path d="m15 33 3.1-9.2L30 11.9a3.4 3.4 0 0 1 4.8 4.8L22.9 28.6 15 33Z" fill="#fff" />
      <path d="m18.1 23.8 4.8 4.8M28.2 13.7l4.8 4.8" stroke="#B6F36B" strokeWidth="2.6" />
    </svg>
  );
}

function StorageLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <path d="M8 14 24 6l16 8-16 8-16-8Z" fill="#E07A2D" />
      <path d="m8 23 16 8 16-8M8 32l16 8 16-8" stroke="#A84E18" strokeWidth="4" strokeLinejoin="round" />
    </svg>
  );
}

function NotionLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <rect x="6" y="6" width="36" height="36" rx="7" fill="#fff" stroke="#171717" strokeWidth="3" />
      <path d="M15 34V14l4-1 14 20V14" stroke="#171717" strokeWidth="4" strokeLinejoin="round" />
      <path d="M13 14h8M29 14h7" stroke="#171717" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

function EmailLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 48 48" fill="none">
      <rect x="5" y="9" width="38" height="30" rx="7" fill="#4F80E8" />
      <path d="m9 14 15 12 15-12" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m9 35 11-10M39 35 28 25" stroke="#DCE8FF" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

/* ────── Platform definitions ────── */

interface PlatformDef {
  id: string;
  name: string;
  tag: string;
  logo: React.ReactNode;
  iconBg: string;
  desc: string;
  pills: string[];
  available: boolean;
  section: 'cn' | 'intl';
}

interface ToolDef {
  id: ToolConnectionType;
  name: string;
  tag: string;
  logo: React.ReactNode;
  iconBg: string;
  desc: string;
  pills: string[];
  available: boolean;
  section: 'dev' | 'data';
}

const PLATFORMS: PlatformDef[] = [
  {
    id: 'feishu',
    name: '飞书 / Lark',
    tag: 'FEISHU',
    logo: <FeishuLogo />,
    iconBg: '#e8f0ff',
    desc: '在飞书群聊中直接与此 Agent 对话，支持富文本卡片、文件收发等飞书原生能力。',
    pills: ['群聊对话', '富文本卡片', '文件收发'],
    available: true,
    section: 'cn',
  },
  {
    id: 'wechat',
    name: '微信',
    tag: 'WECHAT',
    logo: <WeChatLogo />,
    iconBg: '#e8f8e0',
    desc: '扫码连接微信后，此 Agent 自动接入微信消息，可在私聊或群聊中响应。',
    pills: ['私聊回复', '群聊@唤醒', '文件传输'],
    available: true,
    section: 'cn',
  },
  {
    id: 'dingtalk',
    name: '钉钉',
    tag: 'DINGTALK',
    logo: <DingTalkLogo />,
    iconBg: '#e6f2ff',
    desc: '在钉钉群组中接入此 Agent，支持消息卡片、工作通知等钉钉原生能力。',
    pills: ['群聊', '消息卡片', '工作通知'],
    available: false,
    section: 'cn',
  },
  {
    id: 'wecom',
    name: '企业微信',
    tag: 'WECOM',
    logo: <WeComLogo />,
    iconBg: '#e6f4ea',
    desc: '通过企业微信应用接入此 Agent，支持内部沟通与客户服务场景。',
    pills: ['内部群聊', '客户联系', '应用消息'],
    available: false,
    section: 'cn',
  },
  {
    id: 'telegram',
    name: 'Telegram',
    tag: 'TELEGRAM',
    logo: <TelegramLogo />,
    iconBg: '#e3f2fd',
    desc: '通过 Telegram Bot 与此 Agent 交互，支持全球即时通讯。',
    pills: ['私聊', '群聊', 'Markdown'],
    available: false,
    section: 'intl',
  },
  {
    id: 'slack',
    name: 'Slack',
    tag: 'SLACK',
    logo: <SlackLogo />,
    iconBg: '#f3e8f9',
    desc: '在 Slack 频道中直接与此 Agent 协作，支持 Thread 回复。',
    pills: ['频道对话', 'Thread 回复'],
    available: false,
    section: 'intl',
  },
  {
    id: 'discord',
    name: 'Discord',
    tag: 'DISCORD',
    logo: <DiscordLogo />,
    iconBg: '#eaecfb',
    desc: '将此 Agent 部署为 Discord Bot，在服务器频道中交互。',
    pills: ['频道对话', '斜杠命令', 'Embed'],
    available: false,
    section: 'intl',
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    tag: 'WHATSAPP',
    logo: <WhatsAppLogo />,
    iconBg: '#e6f9ec',
    desc: '通过 WhatsApp Business API 接入此 Agent，覆盖全球用户。',
    pills: ['私聊', '模板消息', '多媒体'],
    available: false,
    section: 'intl',
  },
  {
    id: 'teams',
    name: 'Microsoft Teams',
    tag: 'TEAMS',
    logo: <TeamsLogo />,
    iconBg: '#eceefb',
    desc: '在 Microsoft Teams 中与此 Agent 协作，融入企业办公流程。',
    pills: ['频道对话', '会议集成', '自适应卡片'],
    available: false,
    section: 'intl',
  },
];

const TOOLS: ToolDef[] = [
  {
    id: 'git',
    name: 'Git 仓库',
    tag: 'GIT',
    logo: <GitLogo />,
    iconBg: '#fbe7e1',
    desc: '连接 GitHub、GitLab 或 Gitee 仓库，让此 Agent 在授权范围内读取代码、分析变更并参与开发协作。',
    pills: ['代码读取', '提交分析', '仓库协作'],
    available: true,
    section: 'dev',
  },
  {
    id: 'figma',
    name: 'Figma',
    tag: 'DESIGN',
    logo: <FigmaLogo />,
    iconBg: '#f7edff',
    desc: '读取 Figma 文件结构、组件和评论，让 Agent 理解界面设计、设计规范与协作反馈。',
    pills: ['文件结构', '组件理解', '评论读取'],
    available: true,
    section: 'dev',
  },
  {
    id: 'pencil',
    name: 'Pencil',
    tag: 'AI DESIGN',
    logo: <PencilLogo />,
    iconBg: '#eefbdc',
    desc: '通过 Pencil Headless CLI 创建、修改和导出 .pen 设计稿，生成过程固定使用 Codex。',
    pills: ['设计生成', '.pen 文件', '图片 / PDF 导出'],
    available: true,
    section: 'dev',
  },
  {
    id: 'notion',
    name: 'Notion',
    tag: 'WORKSPACE',
    logo: <NotionLogo />,
    iconBg: '#f2f2f2',
    desc: '搜索并读取授权给 Integration 的 Notion 页面与内容块，让 Agent 使用团队文档和项目资料。',
    pills: ['页面搜索', '内容读取', '权限隔离'],
    available: true,
    section: 'dev',
  },
  {
    id: 'email',
    name: '邮箱',
    tag: 'IMAP',
    logo: <EmailLogo />,
    iconBg: '#eaf1ff',
    desc: '只读连接 Gmail、QQ、163 或支持授权码的企业 IMAP 邮箱，让 Agent 搜索和整理真实邮件。',
    pills: ['只读收件箱', '邮件搜索', '正文与附件信息'],
    available: true,
    section: 'data',
  },
  {
    id: 'sql',
    name: 'SQL 数据库',
    tag: 'DATABASE',
    logo: <DatabaseLogo />,
    iconBg: '#e8f0f7',
    desc: '连接 PostgreSQL、MySQL、SQL Server 等数据库，通过只读权限查询真实业务数据。',
    pills: ['Schema 浏览', '只读查询', '数据分析'],
    available: true,
    section: 'data',
  },
  {
    id: 's3',
    name: '对象存储',
    tag: 'S3 / MINIO',
    logo: <StorageLogo />,
    iconBg: '#fff0e2',
    desc: '连接 AWS S3、MinIO 或兼容对象存储，让 Agent 浏览目录并读取授权前缀内的文件。',
    pills: ['对象列表', '文件读取', '前缀隔离'],
    available: true,
    section: 'data',
  },
  {
    id: 'guandata',
    name: '观远 BI',
    tag: 'GUANDATA',
    logo: <GuanDataLogo />,
    iconBg: '#ece9ff',
    desc: '接入观远 BI 的页面、数据集、ETL 与指标体系，让此 Agent 直接理解并分析企业经营数据。',
    pills: ['指标查询', '页面分析', 'ETL 血缘'],
    available: true,
    section: 'data',
  },
];

function IntegrationCard({
  item,
  connected = false,
  onConnect,
  connectionDetail,
}: {
  item: PlatformDef | ToolDef;
  connected?: boolean;
  onConnect?: () => void;
  connectionDetail?: string;
}) {
  return (
    <div className={styles.card}>
      <div className={styles.cardTop}>
        <div className={styles.iconWrap} style={{ background: item.iconBg }}>
          {item.logo}
        </div>
        <div className={styles.cardNameCol}>
          <span className={styles.cardName}>{uiText(item.name)}</span>
          <span className={styles.cardTag}>{item.tag}</span>
        </div>
      </div>
      <div className={styles.cardDesc}>{uiText(item.desc)}</div>
      {connectionDetail && <div className={styles.connectionMeta}>{connectionDetail}</div>}
      <div className={styles.pills}>
        {item.pills.map((pill) => (
          <span key={pill} className={styles.pill}>{uiText(pill)}</span>
        ))}
      </div>
      <div className={styles.cardFooter}>
        {connected ? (
          onConnect ? (
            <button className={styles.manageBtn} onClick={onConnect} aria-label={`${uiText("管理")} ${uiText(item.name)}`}><span>{uiText("✓ 已连接")}</span><b>{uiText("管理")}</b></button>
          ) : (
            <span className={styles.connectedBadge}>{uiText("✓ 已连接")}</span>
          )
        ) : item.available && onConnect ? (
          <button className={styles.connectBtn} onClick={onConnect} aria-label={`${uiText("连接")} ${uiText(item.name)}`}>{uiText("连接")}</button>
        ) : (
          <span className={styles.comingSoon}>{uiText("即将推出")}</span>
        )}
      </div>
    </div>
  );
}

function toolConnectionDetail(connection?: ToolConnectionSummary): string | undefined {
  if (!connection) return undefined;
  const config = connection.config;
  if (connection.type === 'git') return String(config.remoteUrl || 'Git 仓库');
  if (connection.type === 'figma') return Array.isArray(config.allowedFileKeys) && config.allowedFileKeys.length
    ? `${config.allowedFileKeys.length} 个限定文件`
    : '沿用 Figma Token 可见范围';
  if (connection.type === 'pencil') return String(config.workspaceSlug || 'NovaX Agent 工作区');
  if (connection.type === 'notion') return String(config.workspaceLabel || 'Notion 工作区');
  if (connection.type === 'email') return `${String(config.username || '邮箱')} · ${String(config.mailbox || 'INBOX')}`;
  if (connection.type === 'sql') {
    const dialect = config.dialect === 'postgresql' ? 'PostgreSQL'
      : config.dialect === 'mysql' ? 'MySQL' : 'SQL Server';
    return `${dialect} · ${String(config.host || '')}:${String(config.port || '')}/${String(config.database || '')}`;
  }
  if (connection.type === 's3') return `${String(config.bucket || 'Bucket')} · ${String(config.prefix || '全部对象')}`;
  return `${String(config.baseUrl || '观远 BI')} · ${String(config.domain || '')}`;
}

/* ────── Feishu connect modal ────── */
function FeishuModal({ botName, onClose }: { botName: string; onClose: () => void }) {
  const token = useStore((s) => s.token);
  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleConnect = useCallback(async () => {
    if (!appId.trim() || !appSecret.trim()) { setError(uiText("请填写 App ID 和 App Secret")); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/bots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          platform: 'feishu',
          name: `${botName}-feishu`,
          feishuAppId: appId.trim(),
          feishuAppSecret: appSecret.trim(),
          defaultWorkingDirectory: '/tmp',
          description: `${botName} 的飞书连接`,
          installSkills: true,
        }),
      });
      if (res.ok) {
        setSuccess(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `连接失败 (${res.status})`);
      }
    } catch (err: any) {
      setError(err.message || '网络错误');
    } finally {
      setLoading(false);
    }
  }, [appId, appSecret, botName, token]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalIconWrap} style={{ background: '#e8f0ff' }}>
            <FeishuLogo />
          </div>
          <div>
            <div className={styles.modalTitle}>{uiText("连接飞书")}</div>
            <div className={styles.modalSub}>{uiText("将")} {botName} {uiText("接入飞书")}</div>
          </div>
          <div style={{ flex: 1 }} />
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>

        {success ? (
          <div className={styles.modalBody}>
            <div className={styles.successBox}>
              <span className={styles.successIcon}>&#10003;</span>
              <div>
                <div className={styles.successTitle}>{uiText("连接成功")}</div>
                <div className={styles.successDesc}>
                  {uiText("飞书应用已创建，将 Bot 添加到飞书群即可开始使用。")}
                </div>
              </div>
            </div>
            <button className={styles.primaryBtn} onClick={onClose}>{uiText("完成")}</button>
          </div>
        ) : (
          <div className={styles.modalBody}>
            <div className={styles.steps}>
              <div className={styles.step}>
                <span className={styles.stepNum}>1</span>
                <span className={styles.stepText}>
                  {uiText("前往")} <a href="https://open.feishu.cn/app" target="_blank" rel="noreferrer" className={styles.link}>{uiText("飞书开放平台")}</a> {uiText("创建企业自建应用，获取凭证")}
                </span>
              </div>
              <div className={styles.step}>
                <span className={styles.stepNum}>2</span>
                <span className={styles.stepText}>{uiText("将 App ID 和 App Secret 填入下方")}</span>
              </div>
              <div className={styles.step}>
                <span className={styles.stepNum}>3</span>
                <span className={styles.stepText}>{uiText("点击连接，系统自动完成配置")}</span>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>App ID</label>
              <input
                className={styles.formInput}
                placeholder="cli_xxxxxxxxxx"
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>App Secret</label>
              <input
                className={styles.formInput}
                type="password"
                placeholder="xxxxxxxxxxxxxxxx"
                value={appSecret}
                onChange={(e) => setAppSecret(e.target.value)}
              />
            </div>

            {error && <div className={styles.errorMsg}>{error}</div>}

            <button
              className={styles.primaryBtn}
              onClick={handleConnect}
              disabled={loading}
            >
              {loading ? uiText("连接中...") : uiText("连接飞书")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ────── WeChat connect modal ────── */
function WeChatModal({ botName, onClose }: { botName: string; onClose: () => void }) {
  const token = useStore((s) => s.token);
  const [phase, setPhase] = useState<'idle' | 'loading' | 'scanning' | 'scanned' | 'confirmed' | 'creating' | 'success' | 'error'>('idle');
  const [qrUrl, setQrUrl] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [error, setError] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Render QR code to canvas when qrUrl changes
  useEffect(() => {
    if (!qrUrl || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, qrUrl, {
      width: 180,
      margin: 2,
      color: { dark: '#1b1b14', light: '#ffffff' },
    }).catch(() => {});
  }, [qrUrl]);

  const startQr = useCallback(async () => {
    setPhase('loading');
    setError('');
    try {
      const res = await fetch('/api/wechat/qr-start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ botName }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || '生成二维码失败'); setPhase('error'); return; }
      setQrUrl(data.qrUrl);
      setSessionId(data.sessionId);
      setPhase('scanning');
    } catch (err: any) {
      setError(err.message || '网络错误');
      setPhase('error');
    }
  }, [botName, token]);

  // Poll for scan status
  useEffect(() => {
    if (!sessionId || (phase !== 'scanning' && phase !== 'scanned')) return;
    let cancelled = false;
    const poll = async () => {
      while (!cancelled) {
        try {
          const res = await fetch(`/api/wechat/qr-status?session=${encodeURIComponent(sessionId)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          if (cancelled) return;
          if (data.status === 'scanned') setPhase('scanned');
          if (data.status === 'confirmed') {
            setPhase('confirmed');
            // Auto-create bot
            confirmBot(sessionId);
            return;
          }
          if (data.status === 'expired') {
            setError(uiText("二维码已过期，请重新生成"));
            setPhase('error');
            return;
          }
        } catch { /* ignore */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
    };
    poll();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, phase, token]);

  const confirmBot = useCallback(async (sid: string) => {
    setPhase('creating');
    try {
      const res = await fetch('/api/wechat/qr-confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sessionId: sid, botName }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || '创建失败'); setPhase('error'); return; }
      setPhase('success');
    } catch (err: any) {
      setError(err.message || '网络错误');
      setPhase('error');
    }
  }, [botName, token]);

  const statusText = phase === 'loading' ? '正在生成二维码...'
    : phase === 'scanning' ? '等待扫码...'
    : phase === 'scanned' ? '已扫码，请在手机上确认...'
    : phase === 'confirmed' || phase === 'creating' ? '正在创建微信连接...'
    : '';

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalIconWrap} style={{ background: '#e8f8e0' }}>
            <WeChatLogo />
          </div>
          <div>
            <div className={styles.modalTitle}>{uiText("连接微信")}</div>
            <div className={styles.modalSub}>{uiText("将")} {botName} {uiText("接入微信")}</div>
          </div>
          <div style={{ flex: 1 }} />
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>

        {phase === 'success' ? (
          <div className={styles.modalBody}>
            <div className={styles.successBox}>
              <span className={styles.successIcon}>&#10003;</span>
              <div>
                <div className={styles.successTitle}>{uiText("连接成功")}</div>
                <div className={styles.successDesc}>
                  {uiText("微信已连接，Agent 将自动响应消息。服务重启后即可使用。")}
                </div>
              </div>
            </div>
            <button className={styles.primaryBtn} onClick={onClose}>{uiText("完成")}</button>
          </div>
        ) : (
          <div className={styles.modalBody}>
            <div className={styles.steps}>
              <div className={styles.step}>
                <span className={styles.stepNum}>1</span>
                <span className={styles.stepText}>{uiText("点击「生成二维码」获取微信登录码")}</span>
              </div>
              <div className={styles.step}>
                <span className={styles.stepNum}>2</span>
                <span className={styles.stepText}>{uiText("用手机微信扫描二维码并确认登录")}</span>
              </div>
              <div className={styles.step}>
                <span className={styles.stepNum}>3</span>
                <span className={styles.stepText}>{botName} {uiText("自动响应指定聊天中的消息")}</span>
              </div>
            </div>

            {qrUrl ? (
              <div className={styles.qrWrap}>
                <canvas ref={canvasRef} className={styles.qrCanvas} />
                {statusText && <div className={styles.qrStatus}>{statusText}</div>}
              </div>
            ) : (
              <div className={styles.qrBox}>
                {phase === 'loading' ? uiText("生成中...") : uiText("扫码区域")}
              </div>
            )}

            <div className={styles.hint}>
              {uiText("基于微信 iLink 协议，扫码后 Agent 可直接收发私聊消息。建议使用专用微信号作为 Agent 载体。")}
            </div>

            {error && <div className={styles.errorMsg}>{error}</div>}

            {(phase === 'idle' || phase === 'error') && (
              <button className={styles.primaryBtn} onClick={startQr}>
                {uiText("生成二维码")}
              </button>
            )}

            {(phase === 'loading' || phase === 'scanning' || phase === 'scanned' || phase === 'creating') && (
              <button className={styles.primaryBtn} disabled>
                {statusText}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ────── Main panel ────── */
export function ConnectionsPanel({ botName }: ConnectionsPanelProps) {
  const token = useStore((s) => s.token);
  const [modal, setModal] = useState<string | null>(null);
  const [connectedPlatforms, setConnectedPlatforms] = useState<Set<string>>(new Set());
  const [toolConnections, setToolConnections] = useState<Partial<Record<ToolConnectionType, ToolConnectionSummary>>>({});
  const [activeTab, setActiveTab] = useState<'messaging' | 'tools'>('messaging');

  // Check which platforms already have a connected channel for this agent
  useEffect(() => {
    if (!token) return;
    fetch('/api/bots', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => {
        const bot = (data.bots || []).find((b: any) => b.name === botName);
        setConnectedPlatforms(new Set(bot?.channels || []));
      })
      .catch(() => {});
  }, [botName, token]);

  const refreshToolConnections = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`/api/tool-connections?botName=${encodeURIComponent(botName)}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!response.ok) return;
      const data = await response.json();
      const next: Partial<Record<ToolConnectionType, ToolConnectionSummary>> = {};
      for (const connection of data.connections || []) next[connection.type as ToolConnectionType] = connection;
      setToolConnections(next);
    } catch { /* keep the current status during a transient request failure */ }
  }, [botName, token]);

  useEffect(() => {
    void refreshToolConnections();
  }, [refreshToolConnections]);

  const cnPlatforms = PLATFORMS.filter((p) => p.section === 'cn');
  const intlPlatforms = PLATFORMS.filter((p) => p.section === 'intl');
  const devTools = TOOLS.filter((tool) => tool.section === 'dev');
  const dataTools = TOOLS.filter((tool) => tool.section === 'data');

  return (
    <div className={styles.container}>
      <div className={styles.categoryTabs} role="tablist" aria-label={uiText("连接类型")}>
        <button
          className={`${styles.categoryTab} ${activeTab === 'messaging' ? styles.categoryTabActive : ''}`}
          onClick={() => setActiveTab('messaging')}
          role="tab"
          aria-selected={activeTab === 'messaging'}
        >
          {uiText("通信对话 (")}{PLATFORMS.length})
        </button>
        <button
          className={`${styles.categoryTab} ${activeTab === 'tools' ? styles.categoryTabActive : ''}`}
          onClick={() => setActiveTab('tools')}
          role="tab"
          aria-selected={activeTab === 'tools'}
        >
          {uiText("通用工具 (")}{TOOLS.length})
        </button>
      </div>

      {activeTab === 'messaging' ? (
        <div role="tabpanel" className={styles.tabPanel}>
          <p className={styles.tabIntro}>{uiText("把 Agent 接入团队正在使用的即时通讯平台，在群聊或私聊中直接对话。")}</p>

          <div className={styles.sectionLabel}>{uiText("国内平台")}</div>
          <div className={styles.grid}>
            {cnPlatforms.map((platform) => (
              <IntegrationCard
                key={platform.id}
                item={platform}
                connected={connectedPlatforms.has(platform.id)}
                onConnect={platform.available ? () => setModal(platform.id) : undefined}
              />
            ))}
          </div>

          <div className={styles.sectionLabel}>{uiText("海外平台")}</div>
          <div className={styles.grid}>
            {intlPlatforms.map((platform) => (
              <IntegrationCard
                key={platform.id}
                item={platform}
                connected={connectedPlatforms.has(platform.id)}
                onConnect={platform.available ? () => setModal(platform.id) : undefined}
              />
            ))}
          </div>
        </div>
      ) : (
        <div role="tabpanel" className={styles.tabPanel}>
          <p className={styles.tabIntro}>{uiText("连接设计、文档、邮箱、代码与业务数据，让 Agent 在明确授权范围内参与真实工作。")}</p>

          <div className={styles.sectionLabel}>{uiText("创作与协作")}</div>
          <div className={styles.grid}>
            {devTools.map((tool) => (
              <IntegrationCard
                key={tool.id}
                item={tool}
                connected={Boolean(toolConnections[tool.id])}
                connectionDetail={toolConnectionDetail(toolConnections[tool.id])}
                onConnect={() => setModal(tool.id)}
              />
            ))}
          </div>

          <div className={styles.sectionLabel}>{uiText("数据与办公")}</div>
          <div className={styles.grid}>
            {dataTools.map((tool) => (
              <IntegrationCard
                key={tool.id}
                item={tool}
                connected={Boolean(toolConnections[tool.id])}
                connectionDetail={toolConnectionDetail(toolConnections[tool.id])}
                onConnect={() => setModal(tool.id)}
              />
            ))}
          </div>
        </div>
      )}

      {modal === 'feishu' && <FeishuModal botName={botName} onClose={() => setModal(null)} />}
      {modal === 'wechat' && <WeChatModal botName={botName} onClose={() => setModal(null)} />}
      {TOOLS.some((tool) => tool.id === modal) && (() => {
        const tool = TOOLS.find((candidate) => candidate.id === modal)!;
        return (
          <ToolConnectionModal
            type={tool.id}
            botName={botName}
            token={token || ''}
            name={tool.name}
            icon={tool.logo}
            iconBg={tool.iconBg}
            existing={toolConnections[tool.id]}
            onClose={() => setModal(null)}
            onChanged={(connection) => {
              setToolConnections((current) => {
                const next = { ...current };
                if (connection) next[tool.id] = connection;
                else delete next[tool.id];
                return next;
              });
            }}
          />
        );
      })()}
    </div>
  );
}
